import { getRange } from '../../helpers/axisDef';
import { renderGrid, renderXaxisNumerical, renderYaxisNumerical } from '../../zoompan-canvas/renderAxes';
import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpBitmap, TpDataContext, TpDataTable } from '../../../../data/interfaces';
import { getColumnInfo, getBitmap } from '../../../../data/analyticsDataSlice';
import { getSelectionHighlightColor, getStandardDataColor } from '../../../themes/themes';
import {
  LEGEND_ITEM_TYPES,
  renderLegendItems,
  renderXLabel,
  renderYLabel,
  TpLegendItem,
} from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import {
  createColorMappingInfo,
  getMappedColorDataPoint,
  getMappedColorLegendInfo,
  getMappedColorStringDataPoint,
} from '../../helpers/colorMappingInfo';
import { chartDims, createSortIndexes, generateChartTitle, isSelectedItem } from '../../helpers/misc';
import { getLasso2DEnFilterStep } from '../../../../data/selection/selectionEnFilterLasso2D';
import { currentTheme, getThemedGrayFraction } from '../../../themes/themes';
import {
  TpWebGL2DRenderData,
  TpWebGL2DRenderDataBitmap,
  TpWebGL2DRenderDataColoredPoints,
  TpWebGLRenderDataLayer,
} from '../../../../components/webgl-2d/interface-renderData';
import { getDefaultChartColorSchema } from '../../zoompan-canvas/helpers';
import { WebGL2DRenderTypes } from '../../../../components/webgl-2d/interfaces';
import { getUserError } from '../../../../components/miscUtils';
import { color } from '../../helpers/colors';
import { TpDashboardGlobalChartConfig } from '../../dashboard/interfaces';

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  widgetGlobalConfig: TpDashboardGlobalChartConfig,
) {
  const colorSchema = getDefaultChartColorSchema();
  if (config.backgroundColor) {
    colorSchema.backColor = color(config.backgroundColor[0], config.backgroundColor[1], config.backgroundColor[2]);
    if (colorSchema.backColor.r + colorSchema.backColor.g + colorSchema.backColor.b < 3 * 128)
      colorSchema.foreColor = color(255, 255, 255); // in case of dark-ish background, we want white foreground for text etc.
  }

  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable: TpDataTable = boundData.dataTable;
  const dataX = boundData.bindings.x.data;
  const dataY = boundData.bindings.y.data;
  const dataHoverLabel = boundData.bindings.hoverLabel?.data;
  const dataPointSize = boundData.bindings.size?.data;
  const nameX = boundData.bindings.x.columnInfo.name;
  const nameY = boundData.bindings.y.columnInfo.name;
  const title = generateChartTitle(dataContext, dataBinding, config, `${nameX} vs ${nameY}`);
  const dataItemId = boundData.bindings.itemId.data;
  const pointCount = dataX.length;
  const getItemIndexById = boundData.getItemIndexById;

  const colInfoX = getColumnInfo(dataTable, boundData.bindings.x.columnId);
  const colInfoY = getColumnInfo(dataTable, boundData.bindings.y.columnId);

  const filterInfo = getLasso2DEnFilterStep(
    boundData.canonicalTypeInfo.selectionFilter,
    boundData.dataTable.id,
    colInfoX.id,
    colInfoY.id,
  );

  const [minValX, maxValX] = getRange(dataX, 0.05);
  const [minValY, maxValY] = getRange(dataY, 0.05);

  let [minValPointSize, maxValPointSize] = [0, 1];
  if (dataPointSize) [minValPointSize, maxValPointSize] = getRange(dataPointSize, 0.05);

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;

  const opacity = config.opacity;
  const unselectedOpacity = config.unselectedOpacity;
  const pointSizeFactor = config.pointSizeFactor;

  const globalColorMappingConfig = widgetGlobalConfig.colorMapping;
  const colorMappingInfo = createColorMappingInfo(config, boundData, 'color', opacity, globalColorMappingConfig);

  const fastRenderPoint = pointCount > 2500 && !dataPointSize;

  const sortIndex = createSortIndexes(pointCount, true, boundData.bindings.size?.columnInfo, dataPointSize)[0];

  // Background image taken from either the menu selector or the defined ID
  const backgroundImageId = config.backgroundImage ?? config.backgroundImageId;

  let webGL2DRenderData: TpWebGL2DRenderData | null = null;
  if (fastRenderPoint) {
    const renderLayers: TpWebGLRenderDataLayer[] = [];

    const renderBitmapWebGL: TpWebGL2DRenderDataBitmap = {
      renderType: WebGL2DRenderTypes.BITMAP,
      layerId: 'backgroundBitmap',
      isActive: false,
      opacity: config.backgroundBitmapOpacity,
      rectangle: null,
      imageData: null,
    };
    if (backgroundImageId) {
      const bitmapInfo = getBitmap(dataContext, backgroundImageId);
      if (bitmapInfo.rectangle && bitmapInfo.imageData) {
        renderBitmapWebGL.isActive = true;
        renderBitmapWebGL.rectangle = bitmapInfo.rectangle;
        renderBitmapWebGL.imageData = bitmapInfo.imageData;
      }
    }
    renderLayers.push(renderBitmapWebGL);

    const unselColor = getThemedGrayFraction(0.85).opacity(opacity * unselectedOpacity);
    let positsPointsUnSel = new Float32Array(2 * pointCount);
    let colorsPointsUnSel = new Float32Array(4 * pointCount);
    let positsPointsSel = new Float32Array(2 * pointCount);
    let colorsPointsSel = new Float32Array(4 * pointCount);
    let pointIdxSel = 0;
    let pointIdxUnSel = 0;
    for (let pointNr = 0; pointNr < pointCount; pointNr++) {
      const pointNrSorted = sortIndex[pointNr];
      const xVal = dataX[pointNrSorted];
      const yVal = dataY[pointNrSorted];
      const isSelected = isSelectedItem(selectedItemsMap, dataItemId[pointNrSorted]);
      if (!isNaN(xVal) && !isNaN(yVal)) {
        if (isSelected) {
          positsPointsSel[2 * pointIdxSel + 0] = xVal;
          positsPointsSel[2 * pointIdxSel + 1] = yVal;
          let color = getStandardDataColor().opacity(opacity);
          if (colorMappingInfo.hasColors)
            color = getMappedColorDataPoint(colorMappingInfo, pointNrSorted).opacity(opacity);
          colorsPointsSel[4 * pointIdxSel + 0] = color.r / 255;
          colorsPointsSel[4 * pointIdxSel + 1] = color.g / 255;
          colorsPointsSel[4 * pointIdxSel + 2] = color.b / 255;
          colorsPointsSel[4 * pointIdxSel + 3] = color.a;
          pointIdxSel++;
        } else {
          positsPointsUnSel[2 * pointIdxUnSel + 0] = xVal;
          positsPointsUnSel[2 * pointIdxUnSel + 1] = yVal;
          const color = unselColor;
          colorsPointsUnSel[4 * pointIdxUnSel + 0] = color.r / 255;
          colorsPointsUnSel[4 * pointIdxUnSel + 1] = color.g / 255;
          colorsPointsUnSel[4 * pointIdxUnSel + 2] = color.b / 255;
          colorsPointsUnSel[4 * pointIdxUnSel + 3] = color.a;
          pointIdxUnSel++;
        }
      }
    }
    positsPointsSel = positsPointsSel.slice(0, 2 * pointIdxSel);
    colorsPointsSel = colorsPointsSel.slice(0, 4 * pointIdxSel);
    positsPointsUnSel = positsPointsUnSel.slice(0, 2 * pointIdxUnSel);
    colorsPointsUnSel = colorsPointsUnSel.slice(0, 4 * pointIdxUnSel);

    const renderPointsSelWebGL: TpWebGL2DRenderDataColoredPoints = {
      layerId: 'pointsSel',
      renderType: WebGL2DRenderTypes.COLORED_POINTS,
      posits: positsPointsSel,
      colors: colorsPointsSel,
      pointSizeFactor: pointSizeFactor * 4,
    };
    const renderPointsUnSelWebGL: TpWebGL2DRenderDataColoredPoints = {
      layerId: 'pointsUnSel',
      renderType: WebGL2DRenderTypes.COLORED_POINTS,
      posits: positsPointsUnSel,
      colors: colorsPointsUnSel,
      pointSizeFactor: pointSizeFactor * 4,
    };
    renderLayers.push(renderPointsUnSelWebGL);
    renderLayers.push(renderPointsSelWebGL);

    webGL2DRenderData = {
      backColor: colorSchema.backColor,
      layers: renderLayers,
    };
  }

  function renderPoints(renderContext: TpAreaRenderContext) {
    if (fastRenderPoint) return; // we use WebGL in this case

    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;

    if (backgroundImageId) {
      renderBackgroundImage(renderContext, getBitmap(dataContext, backgroundImageId));
    }
    if (!config.hideAxes) renderGrid(renderContext, true, true);

    const standardFillColor = getStandardDataColor().opacity(opacity).toCanvasString();
    const unselColor1 = getThemedGrayFraction(0.85)
      .opacity(opacity * 0.25)
      .toCanvasString();
    const unselColor2 = getThemedGrayFraction(0.7)
      .opacity(unselectedOpacity * opacity)
      .toCanvasString();
    ctx.fillStyle = standardFillColor;
    ctx.strokeStyle = getStandardDataColor()
      .opacity(opacity * 0.3)
      .toCanvasString();
    ctx.lineWidth = renderContext.pixelRatio * 0.75;

    // const pointSizeRect = Math.round(pixelConv(2.5 * pointSizeFactor));

    const whiteBorderColor = currentTheme.colors.background.toCanvasString();

    for (let pointNr = 0; pointNr < pointCount; pointNr++) {
      const pointNrSorted = sortIndex[pointNr];
      const xVal = dataX[pointNrSorted];
      const yVal = dataY[pointNrSorted];
      if (!isNaN(xVal) && !isNaN(yVal)) {
        const scr_x = mapx(xVal);
        const scr_y = mapy(yVal);

        if (colorMappingInfo.hasColors) {
          const [fillColorStr, strokeColorStr] = getMappedColorStringDataPoint(colorMappingInfo, pointNrSorted);
          ctx.fillStyle = fillColorStr;
          ctx.strokeStyle = strokeColorStr;
        } else ctx.fillStyle = standardFillColor;

        // if (fastRenderPoint) {
        //   if (!isSelectedItem(selectedItemsMap, dataItemId[pointNrSorted])) ctx.fillStyle = unselColor1;
        //   ctx.fillRect(
        //     Math.round(scr_x - pointSizeRect / 2),
        //     Math.round(scr_y - pointSizeRect / 2),
        //     pointSizeRect,
        //     pointSizeRect,
        //   );
        // } else {
        let pointSize = 3;
        if (dataPointSize)
          pointSize =
            1 + 8 * Math.sqrt((dataPointSize[pointNrSorted] - minValPointSize) / (maxValPointSize - minValPointSize));
        ctx.beginPath();
        ctx.arc(scr_x, scr_y, pixelConv(pointSize * pointSizeFactor), 0, 2 * Math.PI);
        if (isSelectedItem(selectedItemsMap, dataItemId[pointNrSorted])) {
          ctx.fill();
          if (dataPointSize) {
            ctx.strokeStyle = whiteBorderColor;
            ctx.stroke();
          }
        } else ctx.strokeStyle = unselColor2;
        ctx.stroke();
      }
    }
  }

  function renderBackgroundImage(renderContext: TpAreaRenderContext, bitmap: TpBitmap) {
    const { mapx, mapy } = renderContext;
    const ctx = renderContext.canvasContext;

    if (!bitmap.rectangle || !bitmap.imageData) {
      throw getUserError(`No coordinates or data found for bitmap ${bitmap.id}.`);
    }

    const dx = mapx(bitmap.rectangle.x);
    const dy = mapy(bitmap.rectangle.y);
    const dWidth = mapx(bitmap.rectangle.x + bitmap.rectangle.width) - dx;
    const dHeight = mapy(bitmap.rectangle.y - bitmap.rectangle.height) - dy;

    ctx.drawImage(bitmap.imageData, dx, dy, dWidth, dHeight);
  }

  function renderOverlay(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;

    if (fastRenderPoint) {
      // in webGL we don't draw the gridlines, so we add them here in overlay
      if (!config.hideAxes) renderGrid(renderContext, true, true);
    }

    if (filterInfo && filterInfo.lassoPoints.length > 1) {
      ctx.strokeStyle = getSelectionHighlightColor().opacity(0.3).toCanvasString();
      ctx.fillStyle = getSelectionHighlightColor().opacity(0.07).toCanvasString();
      ctx.setLineDash([]);
      ctx.lineWidth = pixelConv(1);
      ctx.beginPath();
      ctx.moveTo(mapx(filterInfo.lassoPoints[0].x), mapy(filterInfo.lassoPoints[0].y));
      for (let i = 1; i < filterInfo.lassoPoints.length; i++)
        ctx.lineTo(mapx(filterInfo.lassoPoints[i].x), mapy(filterInfo.lassoPoints[i].y));
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
  }

  function renderLegend(renderContext: TpAreaRenderContext) {
    const legendItems: TpLegendItem[] = [];
    if (colorMappingInfo.hasColors) legendItems.push(getMappedColorLegendInfo(colorMappingInfo));
    if (dataPointSize)
      legendItems.push({
        type: LEGEND_ITEM_TYPES.PointSize,
        sizeColumnInfo: getColumnInfo(dataTable, boundData.bindings.size.columnId),
        minValPointSize,
        maxValPointSize,
      });
    renderLegendItems(renderContext, legendItems);
  }

  const hasLegend = (colorMappingInfo.hasColors && !config.hideColorLegend) || dataPointSize;
  const legendBottomSize = hasLegend ? 38 : 0;

  const padding = chartDims.padding;

  const zpLogicalRangeX = createLogicalRange(minValX, maxValX);
  const zpLogicalRangeY = createLogicalRange(minValY, maxValY);
  const zpRenderRangeX = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.yAxisWidth),
    RenderMaxDiffAbs(padding + 0),
  );
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(
      padding + (!config.hideAxes ? chartDims.xAxisHeight + chartDims.xLabelHeight : 0) + legendBottomSize,
    ),
  );

  const areaPoints: TpChartAreaDefinition = {
    id: 'points',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    canAreaSelect: 'xy',
    render: renderPoints,
    renderOverlay,
    forceSquareApectRatio: config.forceSquareApectRatio,
  };

  if (webGL2DRenderData) areaPoints.webGL2DRenderData = webGL2DRenderData;

  const chartAreas: TpChartAreaDefinition[] = [areaPoints];

  if (!config.hideAxes)
    chartAreas.push({
      id: 'xaxis',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      syncXWith: areaPoints,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + chartDims.xAxisHeight + chartDims.xLabelHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + chartDims.xLabelHeight + legendBottomSize),
      ),
      zoomPanX: true,
      render: renderXaxisNumerical,
    });
  if (!config.hideAxes)
    chartAreas.push({
      id: 'xlabel',
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + chartDims.xLabelHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + legendBottomSize),
      ),
      render: (renderContext: TpAreaRenderContext) => renderXLabel(renderContext, nameX),
    });
  if (!config.hideAxes)
    chartAreas.push({
      id: 'yaxis',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMinDiffAbs(padding + chartDims.yAxisWidth)), // offset of Y=4 to make axes overlap
      renderRangeY: zpRenderRangeY,
      syncYWith: areaPoints,
      zoomPanY: true,
      render: renderYaxisNumerical,
    });
  chartAreas.push({
    id: 'ylabel',
    renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
    renderRangeY: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMinDiffAbs(padding + chartDims.headerHeight)),
    render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, !config.hideAxes ? nameY : '', title),
  });

  if (hasLegend) {
    chartAreas.push({
      id: 'legend',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
      render: renderLegend,
    });
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
    zoomPanXYGroup: config.zoomPanXYGroup,
  };

  return {
    definition,
    areaPoints,
    dataTable,
    dataX,
    dataY,
    dataItemId,
    dataHoverLabel,
    getItemIndexById,
    pointCount,
    nameX,
    nameY,
    filterInfo,
    colInfoX,
    colInfoY,
    colInfoHover: boundData.bindings.hoverLabel?.columnId
      ? getColumnInfo(dataTable, boundData.bindings.hoverLabel?.columnId)
      : null,
    colorSchema,
    title,
  };
}
